import axios, {AxiosResponse} from 'axios';
import * as utils from '@/apis/apiUtils';
import type {
  StandardReportCreateParams,
  StandardReportOperationStartTsUpdateParams,
  StandardReportOperationEndTsUpdateParams,
  StandardReportBaseArrivalTsUpdateParams,
  StandardReportContentAndRoadParams,
  StandardReportPhotoParams,
  StandardReportIndexParams,
} from '@/models/apis/externalReports/standard/standardReportRequest';
import type {
  StandardReportResponse,
  StandardReportPhoto,
  StandardReportPhotoCountResponse,
} from '@/models/apis/externalReports/standard/standardReportResponse';
import type {
  StandardReport,
  StandardReportPhotoCount,
} from '@/models/externalReports/standard/spStandardReport';
import { EmptyResponse } from '@/models/index';
import type { StandardReportWithPhotos, StandardReportPhotoExt } from '@/models/externalReports/standard/standardReport';

export default {
  async index(params: StandardReportIndexParams): Promise<StandardReportWithPhotos[]> {
    const { data } = await axios.get<StandardReportResponse[]>(utils.getApiUrl('/external_reports/standard/reports'), {
      params: params || {},
    });
    return convertStandardReportsResponseToStandardReports(data);
  },
  async createStandardReport(params: StandardReportCreateParams): Promise<StandardReport> {
    const { data } = await axios.post<StandardReportResponse>(
      utils.getApiUrl('/external_reports/standard/reports'),
      params,
    );
    return convertStandardReportResponseToStandardReport(data);
  },
  async updateStandardReportStart(
    id: number,
    params: StandardReportOperationStartTsUpdateParams,
  ): Promise<StandardReport> {
    const { data } = await axios.post<StandardReportResponse>(
      utils.getApiUrl(`/external_reports/standard/reports/${id}/start_time`), params);
    return convertStandardReportResponseToStandardReport(data);
  },
  async updateStandardReportEnd(id: number, params: StandardReportOperationEndTsUpdateParams): Promise<StandardReport> {
    const { data } = await axios.post<StandardReportResponse>(
      utils.getApiUrl(`/external_reports/standard/reports/${id}/end_time`),
      params,
    );
    return convertStandardReportResponseToStandardReport(data);
  },
  async updateBaseArrival(id: number, params: StandardReportBaseArrivalTsUpdateParams): Promise<StandardReport> {
    const { data } = await axios.post<StandardReportResponse>(
      utils.getApiUrl(`/external_reports/standard/reports/${id}/base_arrival_time`),
      params,
    );
    return convertStandardReportResponseToStandardReport(data);
  },
  async updateStandardReportContentAndRoad(
    id: number,
    params: StandardReportContentAndRoadParams,
  ): Promise<StandardReport> {
    const { data } = await axios.post<StandardReportResponse>(
      utils.getApiUrl(`/external_reports/standard/reports/${id}`),
      params,
    );
    return convertStandardReportResponseToStandardReport(data);
  },
  async getPhotoCount(id: number): Promise<StandardReportPhotoCount[]> {
    const { data } = await axios.get<StandardReportPhotoCountResponse>(
      utils.getApiUrl(`/external_reports/standard/reports/${id}/photo_count`),
    );
    return convertPhotoCountResponseToPhotoCount(data);
  },
  addPhoto(id: number, params: StandardReportPhotoParams): Promise<AxiosResponse<EmptyResponse>> {
    const formData = utils.transformFormData(params);
    return axios.post(utils.getApiUrl(`/external_reports/standard/reports/${id}/photos`), formData);
  },
  async get(id: number): Promise<StandardReportWithPhotos> {
    const { data } = await axios.get<StandardReportResponse>(
      utils.getApiUrl(`/external_reports/standard/reports/${id}`),
    );
    return convertStandardReportsResponseToStandardReports([data])[0];
  },
};

function convertStandardReportResponseToStandardReport(standardReport: StandardReportResponse): StandardReport {
  return {
    id: standardReport.id,
    contents: standardReport.contents,
    group1Id: standardReport.group1_id,
    standardCarId: standardReport.external_reports_standard_car_id,
    baseDepartureTs: standardReport.base_departure_ts ?? null,
    operationStartTs: standardReport.operation_start_ts ?? null,
    operationEndTs: standardReport.operation_end_ts ?? null,
    baseArrivalTs: standardReport.base_arrival_ts ?? null,
    hanName: standardReport.han_name,
    otherContent: standardReport.other_content,
    roadNames: standardReport.road_names,
  };
}

function convertPhotoCountResponseToPhotoCount(response: StandardReportPhotoCountResponse): StandardReportPhotoCount[] {
  return response.photo_counts.map(item => ({
    photoType: item.photo_type,
    count: item.count,
  }));
}

export function convertStandardReportsResponseToStandardReports(response: StandardReportResponse[]): StandardReportWithPhotos[] {
  return response.map(item => ({
    id: item.id,
    contents: item.contents,
    group1Id: Number(item.group1_id),
    standardCarId: Number(item.external_reports_standard_car_id),
    baseDepartureTs: item.base_departure_ts ?? null,
    operationStartTs: item.operation_start_ts ?? null,
    operationEndTs: item.operation_end_ts ?? null,
    baseArrivalTs: item.base_arrival_ts ?? null,
    hanName: item.han_name,
    otherContent: item.other_content,
    roadNames: item.road_names,
    photos: item.photos.map(photo => convertReportPhotosResponseToReportPhotos(photo)),
  }));
}

export function convertReportPhotosResponseToReportPhotos(response: StandardReportPhoto): StandardReportPhotoExt {
  return {
    id: response.id,
    ts: response.ts,
    lat: Number(response.lat),
    lon: Number(response.lon),
    photoType: response.photo_type,
    imagePath: response.image_path,
    dispOrder: response.disp_order,
    reportContents: '',
    reportRoadNames: '',
    isSelected: false,
    hanName: '',
    savedImage: null,
    samePosPhotos: [],
  };
}
